import { Injectable } from "@angular/core";
import { Area, Assertion, AssertionIssue, Component, db, ImageType, JobIssueVendor, Media, SyncableStatus } from "../../../core/database/db";
import { UploadManagerService } from "../../../core/sync/image-sync/upload-manager.service";
import { MediaForCompression } from "../../media/services/image-compression-pipeline.service";
import { JobIssueVendorService } from "../../jobIssues/services/job-issue-vendor.service";
import { v4 as uuidv4 } from "uuid";

export interface IssueWithDetails {
  issue: AssertionIssue;
  assertion?: Assertion;
  component?: Component;
  area?: Area;
  subtext: string;
}

@Injectable({ providedIn: "root" })
export class AssertionIssuesService {

  constructor(
    private uploadManagerService: UploadManagerService,
    private jobIssueVendorService: JobIssueVendorService
  ) { }

  public getAssertionIssuesForJob(jobId: number, includeDeleted = false) {
    return db.assertionIssue
      .where("jobId")
      .equals(jobId)
      .and(issue => includeDeleted || !issue.deleted)
      .toArray();
  }

  public async getAssertionIssuesForLot(lotId: number, includeDeleted = false) {
    const jobs = await db.inspectionJob
      .where("lotId")
      .equals(lotId)
      .toArray();

    return db.assertionIssue
      .where("jobId")
      .anyOf(jobs.map(job => job.id))
      .and(issue => includeDeleted || !issue.deleted)
      .toArray();
  }

  /**
   * Returns open "carryover" assertion issues for a lot: non-deleted, not-complete assertion issues whose
   * assertionIdentifier belongs to the lot's shared assertions but that do NOT belong to the given job.
   *
   * During a CCM BQ7 audit this surfaces the CM's still-open BQ6 self-inspection findings. Those issues
   * reach the device via the SHARED lot structure (JobAssertion.Issues) under the BQ6 job's id even though
   * the BQ6 job row itself is never synced to CCMs — so we match on assertionIdentifier and exclude the
   * current BQ7 job rather than looking the BQ6 job up directly.
   */
  /** BQ7 issue accepted from an open BQ6 carryover (cloned via parentIdentifier). */
  isAcceptedCarryoverIssue(issue: AssertionIssue): boolean {
    return !!issue.parentIdentifier;
  }

  async updateIssueSeverity(issueId: number, severity: string): Promise<void> {
    await db.assertionIssue.update(issueId, { severity, updatedAt: new Date() });
  }

  public async assignIssueToMe(issue: AssertionIssue) {
    return this.updateAssertionIssue(issue.id!, {
      isSelfAssigned: true,
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending
    }, []);
  }

  public async unassignIssueFromMe(issue: AssertionIssue) {
    return this.updateAssertionIssue(issue.id!, {
      isSelfAssigned: false,
      updatedAt: new Date(),
      syncStatus: SyncableStatus.Pending
    }, []);
  }

  public getOpenCarryoverIssuesForAssertions(assertionIdentifiers: string[], excludeJobId: number): Promise<AssertionIssue[]> {
    if (assertionIdentifiers.length === 0) {
      return Promise.resolve([]);
    }

    return db.assertionIssue
      .where("assertionIdentifier")
      .anyOf(assertionIdentifiers)
      .and(issue => issue.jobId !== excludeJobId && !issue.isComplete && !issue.deleted)
      .toArray();
  }

  /**
   * Returns the non-deleted assertion issues matching the given identifiers. Used to walk an issue's
   * lineage (e.g. a BQ7 job issue -> its BQ7 assertion issue -> the BQ6 finding it was confirmed from).
   */
  public getAssertionIssuesByIdentifiers(identifiers: string[]): Promise<AssertionIssue[]> {
    if (identifiers.length === 0) {
      return Promise.resolve([]);
    }

    return db.assertionIssue
      .where("identifier")
      .anyOf(identifiers)
      .and(issue => !issue.deleted)
      .toArray();
  }

  async updateIssue(issueId: number, text: string, severity: string, media: Media[], dueDate: Date | null, lotAreaIdentifier: string | null): Promise<Record<string, number>> {
    const changes: Partial<Omit<AssertionIssue, "id">> = {
      text,
      severity,
      dueDate: dueDate ?? undefined,
      lotAreaIdentifier: lotAreaIdentifier ?? undefined,
    };

    return this.updateAssertionIssue(issueId, changes, media);
  }

  public async updateAssertionIssue(id: number, changes: Partial<Omit<AssertionIssue, "id">>, media: Media[]): Promise<Record<string, number>> {

    let addedMediaIds: number[] = [];

    const mediaToDelete = media.filter(m => m.deleted === 1);
    const newMedia = media.filter(m => !m.id);

    const mediaToSave: Media[] = newMedia.map(img => {
      const { file: _f, ...rest } = img as MediaForCompression;
      return rest;
    });

    await db.transaction("rw", [db.assertionIssue, db.media, db.syncStatus], async () => {
      await db.assertionIssue.update(id, {
        ...changes,
        syncStatus: SyncableStatus.Pending,
        updatedAt: new Date()
      });
      if (mediaToSave.length > 0) {
        addedMediaIds = await db.media.bulkAdd(mediaToSave, { allKeys: true });
      }
      if (mediaToDelete.length > 0) {
        const mediaIdsToDelete = mediaToDelete.map(m => m.id).filter(id => id !== undefined) as number[];
        await db.softDeleteMedia(mediaIdsToDelete);
      }
    });
    await this.markMediaReadyForUpload(addedMediaIds);

    const mediaFileIdentifierToIdMap = mediaToSave.reduce((acc, media, index) => {
      const mediaId = addedMediaIds[index];
      if (mediaId) {
        acc[media.fileIdentifier] = mediaId;
      }
      return acc;
    }, {} as Record<string, number>);

    return mediaFileIdentifierToIdMap;
  }

  async createIssue(issue: AssertionIssue, media: Media[]): Promise<Record<string, number>> {

    const mediaForIssue = media.map<Media>(m => ({ ...m, parentIdentifier: issue.identifier }));

    const mediaToSave: Media[] = mediaForIssue.map(img => {
      const { file: _f, ...rest } = img as MediaForCompression;
      return rest;
    });

    let addedMediaIds: number[] = [];
    await db.transaction("rw", [db.assertionIssue, db.media, db.syncStatus], async () => {
      await db.assertionIssue.add(issue);
      if (mediaToSave.length > 0) {
        addedMediaIds = await db.media.bulkAdd(mediaToSave, { allKeys: true });
      }
    });
    await this.markMediaReadyForUpload(addedMediaIds);

    const mediaFileIdentifierToIdMap = mediaToSave.reduce((acc, media, index) => {
      const mediaId = addedMediaIds[index];
      if (mediaId) {
        acc[media.fileIdentifier] = mediaId;
      }
      return acc;
    }, {} as Record<string, number>);

    return mediaFileIdentifierToIdMap;
  }

  /**
   * Clones an open assertion issue from the preceding BQ step into a brand new issue on the current step
   * (e.g. BQ6 carrying over to BQ7) and duplicates its issue photos and vendor assignments. The original
   * record is never modified: the new issue points back to it via `parentIdentifier` (the earlier step
   * stays the parent/primary record). This mirrors the BQ8 -> BQ9 parent/child carryover pattern used
   * elsewhere.
   */
  async cloneCarryoverIssueToCurrentStep(
    carryoverIssue: AssertionIssue,
    currentJobId: number,
    severity: string,
    updatedAt: Date,
    vendorsToCarryOver: JobIssueVendor[] = []
  ): Promise<{ newIssue: AssertionIssue; newMedia: Media[]; newVendors: JobIssueVendor[] }> {
    const { id: _originalId, ...carryoverIssueWithoutId } = carryoverIssue;

    // Carried-over issues are created with lotAreaIdentifier already set, so that value is normally
    // carried straight over. The assertion -> component -> area lookup is a defensive fallback for
    // issues that predate the field or arrive without it, mirroring cloneAssertionIssuesToChildJobIssues,
    // so the new issue always stays tied to its area.
    const lotAreaIdentifier = carryoverIssue.lotAreaIdentifier
      ?? await this.resolveLotAreaIdentifierForAssertion(carryoverIssue.assertionIdentifier);

    const newIssue: AssertionIssue = {
      ...carryoverIssueWithoutId,
      identifier: uuidv4(),
      parentIdentifier: carryoverIssue.identifier,
      jobId: currentJobId,
      severity,
      lotAreaIdentifier,
      isComplete: false,
      completedAt: undefined,
      isConfirmedByHomeowner: false,
      confirmedByHomeownerAt: undefined,
      postCompleteStatus: undefined,
      deleted: undefined,
      deletedAt: undefined,
      createdAt: updatedAt,
      updatedAt,
      syncStatus: SyncableStatus.Pending
    };

    const carryoverMedia = await db.media
      .where("parentIdentifier")
      .equals(carryoverIssue.identifier)
      .and(m => m.imageType === ImageType.Issue && !m.deleted)
      .toArray();

    const mediaToClone: Media[] = carryoverMedia.map(media => {
      const { id: _mediaId, ...mediaWithoutId } = media;
      return {
        ...mediaWithoutId,
        parentIdentifier: newIssue.identifier,
        syncStatus: SyncableStatus.Pending,
        updatedAt
      };
    });

    let addedMediaIds: number[] = [];
    let newVendors: JobIssueVendor[] = [];
    await db.transaction("rw", [db.assertionIssue, db.media, db.jobIssueVendor, db.syncStatus], async () => {
      const newId = await db.assertionIssue.add(newIssue);
      newIssue.id = newId as number;
      if (mediaToClone.length > 0) {
        addedMediaIds = await db.media.bulkAdd(mediaToClone, { allKeys: true });
      }
      newVendors = await this.jobIssueVendorService.cloneVendorAssignmentsToIssue(
        vendorsToCarryOver,
        newIssue.identifier,
        updatedAt
      );
    });

    const newMedia: Media[] = mediaToClone.map((media, index) => ({
      ...media,
      id: addedMediaIds[index]
    }));

    return { newIssue, newMedia, newVendors };
  }

  /**
   * Resolves the LotArea identifier for an assertion issue by walking assertion -> component -> area,
   * matching how cloneAssertionIssuesToChildJobIssues derives the area for cloned issues.
   */
  private async resolveLotAreaIdentifierForAssertion(assertionIdentifier: string): Promise<string | undefined> {
    const assertion = await db.assertion.where("identifier").equals(assertionIdentifier)
      .first();
    if (!assertion) {
      return undefined;
    }
    const component = await db.component.get(assertion.componentId);
    if (!component) {
      return undefined;
    }
    const area = await db.area.get(component.areaId);
    return area?.identifier;
  }

  async deleteIssue(issueId: number, parentIdentifier: string): Promise<void> {
    await db.transaction("rw", [db.assertionIssue, db.media, db.syncStatus], async () => {
      const mediaIds = (await db.media.where("parentIdentifier").equals(parentIdentifier)
        .toArray())
        .filter(m => m.id)
        .map(m => m.id!);
      await db.softDeleteMedia(mediaIds);
      await db.softDeleteAssertionIssue(issueId);
    });
  }

  createIssueWithDetails(issue: AssertionIssue, assertions: Assertion[], components: Component[], areas: Area[]): IssueWithDetails {
    const assertion = assertions.find(i => i.identifier === issue.assertionIdentifier);
    const component = components.find(i => i.id === assertion?.componentId);
    const area = areas.find(i => i.id === component?.areaId);

    return {
      issue,
      assertion,
      component,
      area,
      subtext: this.getIssueSubtext(area, assertion)
    };
  }

  private async markMediaReadyForUpload(addedMediaIds: number[]): Promise<void> {
    if (addedMediaIds.length > 0) {
      await this.uploadManagerService.markFilesReadyForUpload(addedMediaIds);
    }
  }

  private getIssueSubtext(area?: Area, assertion?: Assertion): string {
    if (!area || !assertion) {
      return "";
    }

    return `${area?.name ?? "Area"} | ${assertion?.text ?? ""}`;
  }

}
